import { toggleElementFullscreen } from './fullscreen';

export const DEFAULT_KEYBOARD_SMALL_SEEK_SEC = 10;
export const DEFAULT_KEYBOARD_LARGE_SEEK_SEC = 30;
const DOUBLE_TAP_MS = 350;
const DOUBLE_CLICK_GUARD_MS = 400;

export type PlayerKeyboardOptions = {
  video: HTMLVideoElement;
  container: HTMLElement;
  treatAsLive: boolean;
  smallSeekSeconds?: number;
  largeSeekSeconds?: number;
  enableDoubleClickFullscreen?: boolean;
  fullscreenElement?: HTMLElement;
  /** Prefer Shaka UI fullscreen when wired (same element/gesture rules as the control bar). */
  toggleFullscreen?: () => void | Promise<void>;
};

export function isInteractivePlayerTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === 'BUTTON' || tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA' || tag === 'A') {
    return true;
  }
  if (target.isContentEditable) return true;
  if (target.closest('.shaka-settings-menu:not(.shaka-hidden)')) return true;
  if (target.closest('.shaka-overflow-menu:not(.shaka-hidden)')) return true;
  return false;
}

/** Open Shaka menus only — control-bar buttons must not block player keyboard shortcuts. */
export function shouldBlockPlayerKeyboardTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.closest('.shaka-settings-menu:not(.shaka-hidden)')) return true;
  if (target.closest('.shaka-overflow-menu:not(.shaka-hidden)')) return true;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || tag === 'A') return true;
  return false;
}

export function isPlayerShellFocused(container: HTMLElement, video: HTMLVideoElement): boolean {
  const active = document.activeElement;
  if (!active) return false;
  if (active === container || active === video) return true;
  return container.contains(active);
}

function isExternalTextFocus(container: HTMLElement): boolean {
  const active = document.activeElement;
  if (!(active instanceof HTMLElement) || container.contains(active)) return false;
  const tag = active.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}

export function shouldHandlePlayerKeyboard(
  container: HTMLElement,
  video: HTMLVideoElement,
  shellEngaged: boolean,
  event: KeyboardEvent,
): boolean {
  if (!shellEngaged && !isPlayerShellFocused(container, video)) return false;
  if (shouldBlockPlayerKeyboardTarget(event.target)) return false;
  if (isExternalTextFocus(container)) return false;
  return true;
}

export function canKeyboardSeek(video: HTMLVideoElement, treatAsLive: boolean): boolean {
  if (treatAsLive) {
    if (video.seekable.length === 0) return false;
    const start = video.seekable.start(0);
    const end = video.seekable.end(video.seekable.length - 1);
    return Number.isFinite(start) && Number.isFinite(end) && end - start > 1;
  }
  const d = video.duration;
  return Number.isFinite(d) && d > 0;
}

export function clampSeekTime(video: HTMLVideoElement, treatAsLive: boolean, seconds: number): number {
  if (treatAsLive && video.seekable.length > 0) {
    const start = video.seekable.start(0);
    const end = video.seekable.end(video.seekable.length - 1);
    return Math.min(end, Math.max(start, seconds));
  }
  const d = video.duration;
  if (!Number.isFinite(d) || d <= 0) return Math.max(0, seconds);
  return Math.min(d, Math.max(0, seconds));
}

async function togglePlayPause(video: HTMLVideoElement): Promise<void> {
  if (video.paused) {
    await video.play().catch(() => undefined);
  } else {
    video.pause();
  }
}

function applySeekDelta(video: HTMLVideoElement, treatAsLive: boolean, delta: number): void {
  video.currentTime = clampSeekTime(video, treatAsLive, video.currentTime + delta);
}

function applySeekTo(video: HTMLVideoElement, treatAsLive: boolean, position: number): void {
  video.currentTime = clampSeekTime(video, treatAsLive, position);
}

function resolveSeekDelta(
  key: string,
  volumeFocused: boolean,
  smallSeekSeconds: number,
  largeSeekSeconds: number,
): number | null {
  if (key === 'ArrowLeft' && !volumeFocused) return -smallSeekSeconds;
  if (key === 'ArrowRight' && !volumeFocused) return smallSeekSeconds;
  if (key === 'PageDown') return -largeSeekSeconds;
  if (key === 'PageUp') return largeSeekSeconds;
  return null;
}

function handlePlayerKeyDown(
  event: KeyboardEvent,
  options: {
    video: HTMLVideoElement;
    container: HTMLElement;
    treatAsLive: boolean;
    shellEngaged: boolean;
    smallSeekSeconds: number;
    largeSeekSeconds: number;
    requestFullscreen: () => void | Promise<void>;
    seekBlocked: boolean;
  },
): void {
  const {
    video,
    container,
    treatAsLive,
    shellEngaged,
    smallSeekSeconds,
    largeSeekSeconds,
    requestFullscreen,
    seekBlocked,
  } = options;

  if (!shouldHandlePlayerKeyboard(container, video, shellEngaged, event)) return;

  const key = event.key;
  const volumeFocused =
    event.target instanceof HTMLElement && event.target.classList.contains('shaka-volume-bar');

  if (key === ' ' || key === 'k' || key === 'K') {
    event.preventDefault();
    event.stopPropagation();
    void togglePlayPause(video);
    return;
  }

  if (key === 'f' || key === 'F') {
    event.preventDefault();
    event.stopPropagation();
    void requestFullscreen();
    return;
  }

  if (seekBlocked) return;

  if (!canKeyboardSeek(video, treatAsLive)) return;

  if (key === 'Home') {
    event.preventDefault();
    event.stopPropagation();
    const start = treatAsLive && video.seekable.length > 0 ? video.seekable.start(0) : 0;
    applySeekTo(video, treatAsLive, start);
    return;
  }

  if (key === 'End') {
    event.preventDefault();
    event.stopPropagation();
    const end =
      treatAsLive && video.seekable.length > 0
        ? video.seekable.end(video.seekable.length - 1)
        : video.duration;
    applySeekTo(video, treatAsLive, end);
    return;
  }

  const delta = resolveSeekDelta(key, volumeFocused, smallSeekSeconds, largeSeekSeconds);
  if (delta === null) return;

  event.preventDefault();
  event.stopPropagation();
  applySeekDelta(video, treatAsLive, delta);
}

/**
 * Keyboard + fullscreen pointer handling for the custom Shaka shell.
 * Uses document capture listeners so production browsers that never focus the shell still work.
 */
export function attachPlayerKeyboardControls(options: PlayerKeyboardOptions): {
  disconnect: () => void;
  setToggleFullscreen: (fn: () => void | Promise<void>) => void;
  setSeekBlocked: (blocked: boolean) => void;
} {
  const {
    video,
    container,
    treatAsLive,
    smallSeekSeconds = DEFAULT_KEYBOARD_SMALL_SEEK_SEC,
    largeSeekSeconds = DEFAULT_KEYBOARD_LARGE_SEEK_SEC,
    enableDoubleClickFullscreen = true,
    fullscreenElement = container,
    toggleFullscreen,
  } = options;

  if (!container.hasAttribute('tabindex')) {
    container.tabIndex = -1;
  }
  container.setAttribute('role', 'application');
  container.dataset.playerKeyboard = 'attached';

  let shellEngaged = false;
  let seekBlocked = false;
  let toggleFullscreenFn: () => void | Promise<void> =
    toggleFullscreen ?? (() => toggleElementFullscreen(fullscreenElement));

  const requestFullscreen = () => toggleFullscreenFn();

  const setEngagedFromTarget = (target: EventTarget | null) => {
    if (!(target instanceof Node) || !container.contains(target)) {
      shellEngaged = false;
      return;
    }
    if (shouldBlockPlayerKeyboardTarget(target)) return;
    shellEngaged = true;
    if (!isInteractivePlayerTarget(target)) {
      container.focus({ preventScroll: true });
    }
  };

  const onKeyDown = (event: KeyboardEvent) => {
    handlePlayerKeyDown(event, {
      video,
      container,
      treatAsLive,
      shellEngaged,
      smallSeekSeconds,
      largeSeekSeconds,
      requestFullscreen,
      seekBlocked,
    });
  };

  let lastFullscreenGesture = 0;
  const onFullscreenGesture = (event: Event) => {
    if (!enableDoubleClickFullscreen) return;
    if (!container.contains(event.target as Node)) return;
    if (isInteractivePlayerTarget(event.target)) return;

    const now = Date.now();
    if (now - lastFullscreenGesture < DOUBLE_CLICK_GUARD_MS) return;
    lastFullscreenGesture = now;

    event.preventDefault();
    event.stopImmediatePropagation();
    void requestFullscreen();
  };

  let lastTouchEnd = 0;
  const onTouchEnd = (event: TouchEvent) => {
    if (!enableDoubleClickFullscreen) return;
    if (!container.contains(event.target as Node)) return;
    if (isInteractivePlayerTarget(event.target)) return;

    const now = Date.now();
    if (now - lastTouchEnd < DOUBLE_TAP_MS) {
      event.preventDefault();
      lastTouchEnd = 0;
      void requestFullscreen();
    } else {
      lastTouchEnd = now;
    }
  };

  const onPointerDown = (event: PointerEvent) => setEngagedFromTarget(event.target);

  document.addEventListener('keydown', onKeyDown, true);
  document.addEventListener('pointerdown', onPointerDown, true);
  container.addEventListener('dblclick', onFullscreenGesture, true);
  container.addEventListener('touchend', onTouchEnd, { passive: false, capture: true });

  return {
    setToggleFullscreen: (fn) => {
      toggleFullscreenFn = fn;
    },
    setSeekBlocked: (blocked) => {
      seekBlocked = blocked;
    },
    disconnect: () => {
      shellEngaged = false;
      delete container.dataset.playerKeyboard;
      document.removeEventListener('keydown', onKeyDown, true);
      document.removeEventListener('pointerdown', onPointerDown, true);
      container.removeEventListener('dblclick', onFullscreenGesture, true);
      container.removeEventListener('touchend', onTouchEnd, true);
    },
  };
}
